// Mock data — iClean NW modern redesign. Services content kept, layout reimagined.
import {
  Building2, Sparkles, Store, Leaf, ShieldCheck, Clock, Users, Star,
  PhoneCall, GraduationCap, Baby, Home as HomeIcon, Warehouse, Search,
  FileText, Wrench, HeartHandshake, Recycle, ThumbsUp
} from "lucide-react";

export const company = {
  name: "iClean",
  fullName: "iClean NW",
  tagline: "Hassle-free cleaning",
  phone: "0161 667 1895",
  phoneHref: "tel:01616671895",
  email: "info@icleannw.co.uk",
  instagram: "@icleannw",
  instagramHref: "https://instagram.com/icleannw",
  area: "Manchester & the North West",
};

export const nav = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "About", path: "/about" },
  { label: "Careers", path: "/careers" },
  { label: "Contact", path: "/contact" },
];

export const hero = {
  badge: "Commercial · Office · Industrial",
  title: "A spotless space,",
  titleAccent: "without the hassle",
  subtitle:
    "From offices to shops, iClean is your trusted cleaning partner across Manchester and the North West, delivered by a fully vetted, trained team.",
  image: "/generated/hero.jpg?v=4",
  floatImage: "/generated/office.jpg?v=4",
};

export const stats = [
  { value: "7+", label: "Years experience" },
  { value: "3,000+", label: "Houses cleaned" },
  { value: "100%", label: "Vetted & trained staff" },
  { value: "5.0", label: "Average Google rating" },
];

// Original services, kept intact — shown in a modern bento/detail layout.
export const services = [
  {
    id: "contract",
    icon: Building2,
    tag: "Ongoing contracts",
    title: "Contract Cleaning",
    short: "Reliable ongoing cleaning for nurseries, schools and communal areas, with all staff vetted and trained in health & safety before placement.",
    image: "/generated/team.jpg?v=4",
    features: [
      { icon: Baby, name: "Nurseries", desc: "Safe, hygienic cleaning for early-years settings." },
      { icon: HomeIcon, name: "Communal Areas", desc: "Spotless shared spaces for residents and visitors." },
      { icon: GraduationCap, name: "Schools", desc: "Healthy learning environments for pupils and staff." },
    ],
  },
  {
    id: "tenancy",
    icon: Sparkles,
    tag: "One-off deep cleans",
    title: "End of Tenancy & Post-Construction",
    short: "Transform a property from a building site to clean living quarters in a matter of hours, for houses, flats and commercial units.",
    image: "/generated/tenancy2.jpg?v=1",
    features: [
      { icon: HomeIcon, name: "Houses", desc: "Full end-of-tenancy and post-build cleans for homes." },
      { icon: Building2, name: "Flats", desc: "Handover-ready cleans for apartments and flats." },
      { icon: Store, name: "Commercial Units", desc: "Move-in ready cleans for commercial spaces." },
    ],
  },
  {
    id: "commercial",
    icon: Store,
    tag: "Business premises",
    title: "Commercial & Office Cleaning",
    short: "Top-quality cleaning that keeps shops, offices, business premises and warehouses presentable and productive.",
    image: "/generated/office2.jpg?v=1",
    features: [
      { icon: Store, name: "Shops", desc: "Retail spaces that stay welcoming and spotless." },
      { icon: Building2, name: "Offices & Premises", desc: "Productive, presentable workspaces." },
      { icon: Warehouse, name: "Warehouses", desc: "Large-scale industrial cleaning done right." },
    ],
  },
  {
    id: "grounds",
    icon: Leaf,
    tag: "Exterior upkeep",
    title: "Grounds Maintenance",
    short: "Keep the outside of your premises as pristine as the inside: litter picking, bin area cleaning and window cleaning.",
    image: "/generated/grounds.jpg?v=1",
    features: [
      { icon: Recycle, name: "Litter Picking", desc: "Grounds, car parks and communal areas kept clear of litter." },
      { icon: Sparkles, name: "Bin Area Cleaning", desc: "Bin stores and refuse areas cleaned and deodorised." },
      { icon: Search, name: "Window Cleaning", desc: "Streak-free windows for the exterior of your premises." },
    ],
  },
];

export const process = [
  { icon: PhoneCall, step: "01", title: "Get in touch", text: "Tell us about your premises and what you need cleaned, then call or send us a message." },
  { icon: FileText, step: "02", title: "Tailored plan", text: "We build a cleaning package around your schedule, whether one-off or long-term." },
  { icon: ThumbsUp, step: "03", title: "Relax, we clean", text: "Our vetted, trained team delivers spotless results, every single visit." },
];

export const values = [
  { icon: ShieldCheck, title: "Vetted & trained", text: "Every team member is vetted and trained in health & safety before placement." },
  { icon: Clock, title: "Flexible & responsive", text: "From one-off jobs to long-term contracts, we work around you with rapid response." },
  { icon: HeartHandshake, title: "Personal attention", text: "The care we devote to each client gives you total peace of mind." },
  { icon: Recycle, title: "Highest standards", text: "Continuous improvement in quality, service and productivity." },
];

export const gallery = [
  "/generated/back.jpg?v=4",
  "/generated/office.jpg?v=4",
  "/generated/window.jpg?v=4",
  "/generated/tenancy.jpg?v=4",
];

export const clients = [
  "Prima", "Merlin", "Saltire", "Benecare", "Volt Wise", "Expert",
  "Clarity Doors & Windows", "Aril Group", "Greenco", "MH", "BML", "Rent North East",
];

// Real client logos (provided by the business). Each tile bg matches the logo's own background.
export const clientLogos = [
  { name: "Prima Insurance Brokers", src: "/clients/logo_prima.png", bg: "#000000" },
  { name: "MerlineCert", src: "/clients/logo_merline.png", bg: "#ffffff" },
  { name: "Saltire Property Group", src: "/clients/logo_saltire.png", bg: "#f3f3f3" },
  { name: "Expert Property Management", src: "/clients/logo_expert.png", bg: "#0b51a6" },
  { name: "Greenco Property Group", src: "/clients/logo_greenco.png", bg: "#1c1502" },
  { name: "Benecare", src: "/clients/logo_benecare.png", bg: "#228f92" },
  { name: "Bridging Made Easy", src: "/clients/logo_bme.png", bg: "#ffffff" },
  { name: "Aril Group", src: "/clients/logo_aril.png", bg: "#ffffff" },
  { name: "Clarity Doors & Windows", src: "/clients/logo_clarity.png", bg: "#ffffff" },
  { name: "MH Meats", src: "/clients/logo_mhmeats.png", bg: "#ffffff" },
  { name: "BML", src: "/clients/logo_bml.png", bg: "#ffffff" },
  { name: "Halpern's", src: "/clients/logo_halperns.png", bg: "#ffffff" },
  { name: "Selco Residential & Commercial", src: "/clients/logo_selco.png", bg: "#ffffff" },
  { name: "Merline", src: "/clients/logo_merlineco.png", bg: "#ffffff" },
  { name: "Blockcontrol Partnership", src: "/clients/logo_blockcontrol.png", bg: "#094375" },
  { name: "Arrow Property Developments", src: "/clients/logo_arrow.png", bg: "#ffffff" },
];

export const googleMeta = {
  rating: "5.0",
  count: 11,
  url: "https://maps.app.goo.gl/Nw4c1M6fabfQ1ND17",
};

export const reviews = [
  { name: "Clarity Doors & Windows", role: "", rating: 5, when: "9 months ago", color: "#ea731d", text: "Great service, friendly staff. Best pricing." },
  { name: "Voltwise Electrical", role: "", rating: 5, when: "3 years ago", color: "#3b82f6", text: "Best cleaners out there. Polite, honest and brilliant customer service. Have had trouble from other cleaning companies in the past, I cannot recommend this company enough." },
  { name: "MH Meats", role: "", rating: 5, when: "3 years ago", color: "#10b981", text: "Such an easy experience. I called up and they came over to quote within 24hrs, and within another 24hrs we had a new cleaner." },
  { name: "Newswift Management", role: "", rating: 5, when: "3 years ago", color: "#8b5cf6", text: "Top top customer service and the job is always checked over by a manager before final invoice sent." },
  { name: "Selco Estates", role: "", rating: 5, when: "3 years ago", color: "#ef4444", text: "Top class service. Really impressed with the speed of service and the quality of the work." },
  { name: "Rent North East", role: "", rating: 5, when: "3 years ago", color: "#f59e0b", text: "Amazing company, very efficient, left everything cleaner than it was when new. Highly recommended." },
];

export const faqs = [
  { q: "Which areas do you cover?", a: "We cover Manchester and the wider North West. Get in touch and we’ll confirm we can reach your premises." },
  { q: "Are your staff vetted?", a: "Yes, all staff are fully vetted and trained in all health and safety procedures before placement in your premises." },
  { q: "Do you offer one-off cleans?", a: "Absolutely. Whether it’s a small one-off job or a long-term service contract, we’re here to help." },
  { q: "How do I get a quote?", a: "Call us on 0161 667 1895 or send a message through our contact form and we’ll get back to you as soon as we can." },
];
